import React, { useContext, useRef } from 'react'
import { node } from 'prop-types'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Helmet from 'react-helmet'
import { TextContext } from '..'
import { getMetaElementByName } from 'settings/app/helpers'
import { __dev__, __prod__ } from 'settings/app/constants'

/**
 * global hoc for meta information
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
const MetaInformation = ({ children }) => {
  const location = useLocation().pathname
  const textContext = useContext(TextContext)
  const { current: typoSeoTitleRef } = useRef(document.title)
  const { current: typoSeoDescriptionRef } = useRef(
    getMetaElementByName('description')
  )

  // local && global state
  const [currentView, setCurrentView] = React.useState('')
  const state = useSelector((state) => state)
  const { detailData } = state

  React.useEffect(() => {
    let view = ''
    if (location.includes('merkliste')) {
      view = 'merkliste'
    } else if (location.includes('vergleich')) {
      view = 'vergleich'
    } else if (location.includes('krankenhaus')) {
      view = 'detailHospital'
    } else if (location.includes('arzt')) {
      view = 'detailDoctor'
    } else {
      view = 'suche'
    }
    setCurrentView(view)
  }, [location])

  /**
   * meta title
   * @returns {string}
   */
  const getSeoTitle = () => {
    let seoTitle

    // hospital detail view && detail data is complete loaded
    if (currentView === 'detailHospital' && state?.detailData) {
      seoTitle = `${ detailData.result.locationName } in ${ detailData.result.address.zip } ${ detailData.result.address.city }`
      if (seoTitle.length < 50)
        seoTitle = `${ seoTitle } | AOK-Gesundheitsnavigator`
    } else {
      seoTitle = __dev__
        ? textContext['meta.general.title']
        : typoSeoTitleRef.replace('| AOK - Die Gesundheitskasse', '')
    }

    return seoTitle
  }

  /**
   * meta description
   * @returns {string}
   */
  const getSeoDescription = () => {
    let seoDescription

    // hospital detail view && detail data is complete loaded
    if (currentView === 'detailHospital' && state?.detailData) {
      seoDescription = `${ detailData.result.locationName } ${ detailData.result.address.zip }, ${ detailData.result.address.city } ${ detailData.result.address.street }.`
      if (detailData.result?.infos && detailData.result.infos.length > 0) {
        seoDescription = `${ seoDescription } ${ detailData.result.infos
          .map((info) => info.label)
          .join('. ') }.`
      }
    } else {
      // when production found description then use these otherwise use fallback
      seoDescription =
        __dev__ || (__prod__ && !typoSeoDescriptionRef)
          ? textContext['meta.general.description']
          : typoSeoDescriptionRef.getAttribute('content')
    }
    return seoDescription
  }

  const getMetaRobots = () => {
    if (
      /\/pk\/(?!uni).*\/(medizin-versorgung\/gesundheitsnavigator\/facharztgebiete)/i.test(
        location
      )
    ) {
      return <meta name='robots' content='index,nofollow' />
    }

    return null
  }

  return (
    <>
      {(('' !== getSeoTitle() && '' !== getSeoDescription()) ||
        !!getMetaRobots()) && (
        <Helmet htmlAttributes={{ lang: 'de-DE', dir: 'ltr' }}>
          {'' !== getSeoTitle() && '' !== getSeoDescription() && (
            <title>{getSeoTitle()}</title>
          )}
          {'' !== getSeoTitle() && '' !== getSeoDescription() && (
            <meta name='description' content={ getSeoDescription() } />
          )}
          {getMetaRobots()}
        </Helmet>
      )}
      {children}
    </>
  )
}

MetaInformation.propTypes = {
  children: node.isRequired,
}

export default MetaInformation
